import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ShoppingCart,
  Package,
  Receipt,
  Users,
  TrendingUp,
  TrendingDown,
  Activity,
  DollarSign,
  Eye,
  BarChart3,
  RefreshCw,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/services/api';
import Button from '@/components/ui/Button';

interface DashboardStats {
  todaySales: number;
  todaySalesChange: number;
  totalProducts: number;
  productsChange: number;
  todayTransactions: number;
  transactionsChange: number;
  activeUsers: number;
  usersChange: number;
  lowStockProducts: number;
  pendingMRAInvoices: number;
  monthlyRevenue: number;
  monthlyRevenueChange: number;
}

interface RecentTransaction {
  id: string;
  invoiceNumber: string;
  customerName?: string;
  totalAmount: number;
  paymentMethod: string;
  paymentStatus: string;
  issuedAt: string;
  mraStatus?: string;
}

const DashboardPage: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState<'today' | 'week' | 'month'>('today');
  const queryClient = useQueryClient();

  // Fetch real dashboard stats
  const { data: statsData, isLoading: isLoadingStats } = useQuery<DashboardStats>({
    queryKey: ['dashboard', 'stats', timeRange],
    queryFn: async () => {
      const res = await api.get(`/api/dashboard/stats?timeRange=${timeRange}`);
      return res.data.data;
    },
  });

  // Fetch real recent transactions
  const { data: transactionsData, isLoading: isLoadingTransactions } = useQuery<RecentTransaction[]>({
    queryKey: ['dashboard', 'recent-transactions'],
    queryFn: async () => {
      const res = await api.get('/api/dashboard/recent-transactions?limit=5');
      return res.data.data;
    },
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-MW', {
      style: 'currency',
      currency: 'MWK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  const getChangeIcon = (change: number) => {
    return change > 0 ? TrendingUp : change < 0 ? TrendingDown : Activity;
  };

  const getChangeColor = (change: number) => {
    if (change > 0) return 'text-green-600';
    if (change < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const statsCards = [
    {
      title: 'Today\'s Sales',
      value: formatCurrency(statsData?.todaySales || 0),
      change: statsData?.todaySalesChange || 0,
      icon: DollarSign,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      onClick: () => navigate('/sales'),
    },
    {
      title: 'Products',
      value: statsData?.totalProducts?.toLocaleString() || '0',
      change: statsData?.productsChange || 0,
      icon: Package,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-100',
      onClick: () => navigate('/products'),
    },
    {
      title: 'Transactions',
      value: statsData?.todayTransactions?.toLocaleString() || '0',
      change: statsData?.transactionsChange || 0,
      icon: Receipt,
      color: 'text-violet-600',
      bgColor: 'bg-violet-100',
      onClick: () => navigate('/sales'),
    },
    {
      title: 'Active Users',
      value: statsData?.activeUsers?.toString() || '0',
      change: statsData?.usersChange || 0,
      icon: Users,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
      onClick: () => navigate('/users'),
    },
  ];

  const alertCards = [
    {
      title: 'Low Stock',
      value: statsData?.lowStockProducts || 0,
      icon: Package,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100',
      description: 'Products need restocking',
      onClick: () => navigate('/products'),
    },
    {
      title: 'Pending MRA',
      value: statsData?.pendingMRAInvoices || 0,
      icon: Activity,
      color: 'text-red-600',
      bgColor: 'bg-red-100',
      description: 'Invoices awaiting MRA approval',
      onClick: () => navigate('/sales'),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Welcome back, {user?.email}!
              </h1>
              <p className="mt-2 text-gray-600">
                Here's what's happening with your business today.
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  // Refresh all dashboard queries
                  queryClient.invalidateQueries({ queryKey: ['dashboard'] });
                }}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={async () => {
                  try {
                    await api.post('/seed');
                    alert('Sample data seeded successfully! Refresh the page to see the data.');
                    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
                  } catch (error) {
                    alert('Failed to seed sample data');
                  }
                }}
              >
                Seed Sample Data
              </Button>
              <div className="flex bg-white rounded-lg shadow-sm p-1">
                {(['today', 'week', 'month'] as const).map((range) => (
                  <button
                    key={range}
                    onClick={() => setTimeRange(range)}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      timeRange === range
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    {range.charAt(0).toUpperCase() + range.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {isLoadingStats ? (
            Array.from({ length: 8 }).map((_, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
                <div className="h-8 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/3"></div>
              </div>
            ))
          ) : (
            statsCards.map((stat, index) => (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                onClick={stat.onClick}
                className="bg-white rounded-xl shadow-lg p-6 cursor-pointer hover:shadow-xl transition-all duration-200"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                    <stat.icon className={`h-6 w-6 ${stat.color}`} />
                  </div>
                  <div className={`flex items-center space-x-1 ${getChangeColor(stat.change)}`}>
                    {React.createElement(getChangeIcon(stat.change), { className: "h-4 w-4" })}
                    <span className="text-sm font-medium">
                      {stat.change > 0 ? '+' : ''}{stat.change}%
                    </span>
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">{stat.value}</h3>
                  <p className="text-sm text-gray-600 mt-1">{stat.title}</p>
                </div>
              </motion.div>
            ))
          )}
        </div>

        {/* Alert Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {alertCards.map((alert, index) => {
            const Icon = alert.icon;
            return (
              <motion.div
                key={alert.title}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 + 0.4 }}
                whileHover={{ scale: 1.02 }}
                onClick={alert.onClick}
                className="bg-white rounded-xl shadow-lg p-6 cursor-pointer hover:shadow-xl transition-all duration-200"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${alert.bgColor}`}>
                      <Icon className={`h-5 w-5 ${alert.color}`} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{alert.title}</h4>
                      <p className="text-sm text-gray-600">{alert.description}</p>
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-gray-900">{alert.value}</div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Monthly Revenue Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="lg:col-span-2 bg-white rounded-xl shadow-lg p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Monthly Revenue</h3>
              <div className="flex items-center space-x-2">
                <BarChart3 className="h-5 w-5 text-gray-400" />
                <span className="text-sm text-gray-600">This month</span>
              </div>
            </div>
            <div className="mb-4">
              <div className="text-3xl font-bold text-gray-900">
                {formatCurrency(statsData?.monthlyRevenue || 0)}
              </div>
              <span className="flex items-center">
                {React.createElement(getChangeIcon(statsData?.monthlyRevenueChange || 0), { className: "h-4 w-4 mr-1" })}
                <span>{statsData?.monthlyRevenueChange ? (statsData.monthlyRevenueChange > 0 ? '+' : '') + statsData.monthlyRevenueChange + '%' : '0%'} from last month</span>
              </span>
            </div>
            <div className="h-48 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg flex items-center justify-center">
              <BarChart3 className="h-12 w-12 text-blue-300" />
              <p className="ml-2 text-blue-600">Revenue chart would be displayed here</p>
            </div>
          </motion.div>

          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Quick Actions</h3>
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/pos')}
                className="w-full justify-start"
                variant="primary"
              >
                <ShoppingCart className="h-4 w-4 mr-3" />
                New Sale
              </Button>
              <Button
                onClick={() => navigate('/products')}
                className="w-full justify-start"
                variant="secondary"
              >
                <Package className="h-4 w-4 mr-3" />
                Add Product
              </Button>
              <Button
                onClick={() => navigate('/reports')}
                className="w-full justify-start"
                variant="secondary"
              >
                <BarChart3 className="h-4 w-4 mr-3" />
                View Reports
              </Button>
            </div>
          </motion.div>
        </div>

        {/* Recent Transactions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Recent Transactions</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/sales')}
            >
              <Eye className="h-4 w-4 mr-2" />
              View All
            </Button>
          </div>
          <div className="space-y-4">
            {isLoadingTransactions ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Loading transactions...</p>
              </div>
            ) : transactionsData?.length === 0 ? (
              <div className="text-center py-8">
                <Receipt className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No transactions yet</p>
              </div>
            ) : (
              transactionsData?.map((transaction, index) => (
                <motion.div
                  key={transaction.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.9 + index * 0.1 }}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                  onClick={() => navigate(`/sales`)}
                >
                  <div className="flex items-center space-x-4">
                    <div className={`p-2 rounded-lg ${
                      transaction.paymentStatus === 'paid' ? 'bg-green-100' : 'bg-yellow-100'
                    }`}>
                      <Receipt className={`h-4 w-4 ${
                        transaction.paymentStatus === 'paid' ? 'text-green-600' : 'text-yellow-600'
                      }`} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{transaction.invoiceNumber}</p>
                      <p className="text-sm text-gray-600">{transaction.customerName}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">{formatCurrency(transaction.totalAmount)}</p>
                    <div className="flex items-center space-x-2 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        transaction.mraStatus === 'approved' ? 'bg-green-100 text-green-800' :
                        transaction.mraStatus === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {transaction.mraStatus || 'pending'}
                      </span>
                      <span className="text-gray-500">{formatTimeAgo(transaction.issuedAt)}</span>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default DashboardPage;
